import { useCallback, useEffect, useState } from 'react';
import copy from 'copy-to-clipboard';
import { trackEvent } from '../../analytics/sml/analytics';

/** How long the "Link copied" toast stays on screen. */
export const LINK_COPIED_TOAST_MS = 2400;

/** The address of the share page, with no query and no hash. */
function pageUrl(): string {
  if (typeof window === 'undefined') {
    return '';
  }
  return `${window.location.origin}${window.location.pathname}`;
}

export interface ShareLink {
  /** True while the "Link copied" toast shows. */
  copied: boolean;
  /** Copies the address of the page and shows the toast. */
  copyLink: () => void;
  /** Opens the share sheet of the device; with no share sheet, copies the address (design asset-share c4). */
  shareLink: () => void;
}

/** Copy link and Share of the public asset share page. */
export function useShareLink(title: string, kind: 'kitAsset' | 'reel'): ShareLink {
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!copied) {
      return undefined;
    }
    const timer = setTimeout(() => setCopied(false), LINK_COPIED_TOAST_MS);
    return () => clearTimeout(timer);
  }, [copied]);

  const copyLink = useCallback(() => {
    copy(pageUrl());
    setCopied(true);
    trackEvent('public_share_cta_tapped', { kind, cta: 'other' });
  }, [kind]);

  const shareLink = useCallback(() => {
    if (typeof navigator !== 'undefined' && typeof navigator.share === 'function') {
      trackEvent('public_share_cta_tapped', { kind, cta: 'other' });
      navigator.share({ title, url: pageUrl() }).catch((error: unknown) => {
        // The user closed the share sheet: nothing to do.
        if (error instanceof Error && error.name === 'AbortError') {
          return;
        }
        console.error(error);
      });
      return;
    }
    copyLink();
  }, [copyLink, kind, title]);

  return { copied, copyLink, shareLink };
}
